import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { addUserAddress, getAllState, getUserAddress } from "service";
import { toast } from "sonner";

export interface IAllStateResponse {
  count: number;
  next: string;
  previous: any | null;
  results: IState[];
}

export interface IState {
  pk: number;
  name: string;
  lgas: ILGA[];
}

export interface ILGA {
  pk: number;
  name: string;
}

interface IFormData {
  address: string;
  state: string;
  lga: string;
  bus_stop: string;
  active: boolean;
}

interface IAddressResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IAddress[];
}

export interface IAddress {
  id: string;
  address: string;
  state: string;
  lga: string;
  bus_stop: string;
  active: boolean;
}

export function useCore() {
  const queryClient = useQueryClient();



  const [formData, setFormData] = useState<IFormData>({
    address: "",
    state: "",
    lga: "",
    bus_stop: "",
    active: true,
  });

  const { data: allStateData, isLoading: allStateLoading, error } =
    useSuspenseQuery<IAllStateResponse>({
      queryKey: ["all-states"],
      queryFn: async () => {
        const response = await getAllState();
        return response;
      },
    });

  const { data: userAddress, isLoading: userAddressLoading } =
    useQuery<IAddressResponse>({
      queryKey: ["user-address"],
      queryFn: async () => {
        const response = await getUserAddress();
        return response;
      },
    });

  const { mutate: addUserAddressMutation, isPending: addUserAddressIsPending } =
    useMutation({
      mutationFn: async () => {
        const body = {
          address: formData.address,
          state: String(formData.state),
          lga: String(formData.lga),
          bus_stop: formData.bus_stop,
          active: formData.active,
        };
        const response = await addUserAddress(body);
        return response;
      },
      onSuccess: async (data) => {
        toast.success("Address added successfully");
        await queryClient.invalidateQueries({
          queryKey: ["user-address"],
        });
      },
      onError: (err) => {
        toast.error("Failed to add address");
      },
    });

  useEffect(() => {
    if (error) {
      console.log(error)
      // toast.error("Error fetching states")
    }
  }, [error])

  const allStateOption = allStateData?.results.map((state) => ({
    label: state.name,
    value: state.pk,
  }));

  const selectedState = allStateData?.results.find(
    (state) => state.pk === Number(formData.state)
  );

  const allLgaOption =
    selectedState?.lgas.map((lga) => ({
      label: lga.name,
      value: lga.pk,
    })) || [];

  const handleFieldChange = (
    field: keyof IFormData,
    value: string | number | boolean
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleAddUserAddress = () => {
    addUserAddressMutation();
  };

  const isFormComplete = () => {
    return (
      formData.address && formData.state && formData.lga && formData.bus_stop
    );
  };

  return {
    allStateData,
    isLoading: allStateLoading || addUserAddressIsPending,
    userAddress,
    userAddressLoading,
    allStateOption,
    allLgaOption,
    formData,
    handleFieldChange,
    handleAddUserAddress,
    isFormComplete,
  };
}
